import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { useState } from 'react';
import { UI_LOGO } from '@bwoty-web/ui-kit/icons';
import { useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import { Carousel, Grid, GridItem, RoomCard, Icon, Tag } from '@bwoty-web/ui-kit';
import OnRequestPopup from './OnRequestPopup';
import CarouselSwitchButton from '../shared/carouselSwitchButton/CarouselSwitchButton';
import SkipLink from '../shared/skipLink/SkipLink';
import './roomCardList.scss';

const RoomCardList = ({
  hotel = null,
  isHotelOnly = false,
  labels,
  searchInformation: { numberOfAdults, numberOfChildren },
  selectRoomGroup,
  showRoomTypeLink = false,
}) => {
  const [isOnRequestPopupOpen, setIsOnRequestPopupOpen] = useState(false);
  const [showCardsInCarousel, setShowCardsInCarousel] = useState(!isHotelOnly);
  const isGlobetrotter = useIsGlobetrotter();

  const isDesktop = window !== 'undefined' && window.innerWidth > 1023;
  const hideToggleButton = isDesktop && hotel?.roomGroups?.length < 4;
  const toggleCardViewHandler = () => setShowCardsInCarousel((prevState) => !prevState);

  const roomPresentationMode = hotel.noPrices;

  const buildRoomCard = (roomGroup) => {
    const { amenities, bedTypes, bookingEngineRoomGroupKey, hasCharges, priceInfo, rooms, roomType } = roomGroup;

    const { isDiscounted, ordinaryPrice, totalPrice, twoAdultsPricePerPerson } = priceInfo || {};

    const unavailableRoom = !priceInfo && !rooms[0].isAvailable;

    const getBedTypeDescription = ({ description, type }) => {
      const bedTypeDescription = labels[type.toLowerCase()];
      if (bedTypeDescription) return bedTypeDescription;
      return description;
    };

    const renderBedType = (bedTypesList) =>
      bedTypesList.map((bedType) => `${bedType.quantity} ${getBedTypeDescription(bedType)}`);

    const renderTitle = (roomsList) =>
      roomsList.map(({ description, quantity }) => `${description} (${quantity} ${labels.numberOfSuffix})`);

    const openRoomModal = (roomId) => {
      if (roomId && typeof window !== 'undefined' && window) {
        window.postMessage(`cmd.open.popup:${roomId}`);
      }
    };

    const clickOnRequestLink = () => {
      setIsOnRequestPopupOpen(true);
    };

    const selectRoomGroupHandler = () => {
      if (roomGroup === hotel.selectedRoomGroup) return;
      selectRoomGroup(roomGroup);
    };

    const roomId = roomType?.wvId || roomType?.externalId;
    const selectedRoom = roomPresentationMode
      ? null
      : bookingEngineRoomGroupKey === hotel.selectedRoomGroup.bookingEngineRoomGroupKey;
    const showFallbackImage = !roomType?.cloudinaryUriTemplate && isGlobetrotter;

    const fallbackImage = (
      <div className="iw-room-card-list__default-room-image">
        <Icon
          path={UI_LOGO}
          size="xl"
        />
      </div>
    );

    const nonRefundableTag = (
      <Tag
        className="iw-room-card-list__non-refundable-tag"
        variant="alert"
      >
        {labels.paymentLabel}
      </Tag>
    );

    const notBookableTag = (
      <Tag
        className="iw-room-card-list__non-refundable-tag"
        variant="light-gray"
      >
        {labels.notBookable}
      </Tag>
    );

    let additionalContent;
    if (hasCharges) additionalContent = nonRefundableTag;
    if (unavailableRoom && !roomPresentationMode) additionalContent = notBookableTag;

    const pricePerPerson = unavailableRoom ? '' : twoAdultsPricePerPerson;

    let buttonVariant = 'tertiary';
    if (selectedRoom) buttonVariant = 'primary';

    let buttonText;
    if (selectedRoom) buttonText = labels.roomSelectedBtnLabel;
    if (unavailableRoom) buttonText = labels.soldOut;
    if (roomPresentationMode) buttonText = labels.searchForPrices;

    const linkList = [];
    if (roomId && showRoomTypeLink) {
      linkList.push({
        'aria-haspopup': 'dialog',
        onClick: () => openRoomModal(roomId),
        text: labels.readMoreRoom,
      });
    }
    if (rooms.some((room) => room.isAvailable === false) && !unavailableRoom) {
      linkList.push({
        'aria-haspopup': 'dialog',
        onClick: clickOnRequestLink,
        text: labels.availableOnRequest,
      });
    }

    return (
      <RoomCard
        additionalCardContent={additionalContent}
        bullets={bedTypes ? renderBedType(bedTypes) : []}
        buttonText={buttonText}
        buttonVariant={buttonVariant}
        columnSpan={4}
        description={roomType?.contentTexts[0]}
        discountDisplayType={isDiscounted ? 'original' : 'hide'}
        externalImage={showFallbackImage ? fallbackImage : undefined}
        hideImage={isHotelOnly}
        image={{ cloudinaryUriTemplate: roomType?.cloudinaryUriTemplate }}
        key={uniqid('room-card-', bookingEngineRoomGroupKey)}
        ksps={!unavailableRoom && amenities.map((amenity) => amenity.name)}
        linkList={linkList}
        onClick={roomPresentationMode ? () => {} : selectRoomGroupHandler}
        originalPrice={unavailableRoom ? undefined : ordinaryPrice}
        price={unavailableRoom ? undefined : totalPrice}
        {...(!unavailableRoom &&
          numberOfAdults === 2 &&
          numberOfChildren === 0 && {
            pricePerPerson,
          })}
        {...(unavailableRoom && !roomPresentationMode && { url: labels.contactUsUrlPathName })}
        title={renderTitle(rooms).join(`\n\n`)}
        onClickElementProps={roomPresentationMode ? { disabled: true } : {}}
      />
    );
  };

  const renderRoomCards = () =>
    hotel.roomGroups.map((roomGroup) =>
      showCardsInCarousel ? (
        buildRoomCard(roomGroup)
      ) : (
        <GridItem
          columnSpan={4}
          key={roomGroup.bookingEngineRoomGroupKey}
        >
          {buildRoomCard(roomGroup)}
        </GridItem>
      ),
    );

  return (
    <div className="iw-room-card-list">
      <SkipLink
        href="#price-summary"
        linkText={labels?.skipRoomListLabel}
      />
      {!hideToggleButton && (
        <div className="iw-room-card-list__header-container">
          <CarouselSwitchButton
            label={labels.carouselSwitchButtonLabel}
            showCardsInCarousel={showCardsInCarousel}
            toggleCardView={toggleCardViewHandler}
          />
        </div>
      )}
      {showCardsInCarousel ? <Carousel>{renderRoomCards()}</Carousel> : <Grid>{renderRoomCards()}</Grid>}
      <OnRequestPopup
        labels={labels}
        isOpen={isOnRequestPopupOpen}
        hidePopup={() => setIsOnRequestPopupOpen(false)}
      />
    </div>
  );
};

RoomCardList.propTypes = {
  hotel: PropTypes.shape({
    selectedRoomGroup: PropTypes.shape({
      bookingEngineRoomGroupKey: PropTypes.string.isRequired,
    }),
    roomGroups: PropTypes.arrayOf(PropTypes.shape({})),
    noPrices: PropTypes.bool,
  }),
  isHotelOnly: PropTypes.bool,
  labels: PropTypes.shape({
    availableOnRequest: PropTypes.string.isRequired,
    carouselSwitchButtonLabel: PropTypes.string,
    chooseRoomType: PropTypes.string,
    contactUsUrlPathName: PropTypes.string,
    flightDetailsLink: PropTypes.string,
    notBookable: PropTypes.string,
    numberOfSuffix: PropTypes.string.isRequired,
    paymentLabel: PropTypes.string,
    readMoreRoom: PropTypes.string,
    roomSelectedBtnLabel: PropTypes.string,
    searchForPrices: PropTypes.string,
    soldOut: PropTypes.string,
    skipRoomListLabel: PropTypes.string,
  }).isRequired,
  searchInformation: PropTypes.shape({
    numberOfAdults: PropTypes.number,
    numberOfChildren: PropTypes.number,
  }).isRequired,
  selectRoomGroup: PropTypes.func.isRequired,
  showRoomTypeLink: PropTypes.bool,
};

export default RoomCardList;
